// src/types.ts
var FALLBACK_MODELS = [
  // OpenAI
  { provider: "openai", model: "gpt-4o-mini", displayName: "GPT-4o-mini" },
  { provider: "openai", model: "gpt-4o", displayName: "GPT-4o" },
  // Claude
  {
    provider: "claude",
    model: "claude-3-5-sonnet-20241022",
    displayName: "Claude 3.5 Sonnet"
  },
  {
    provider: "claude",
    model: "claude-3-5-haiku-20241022",
    displayName: "Claude 3.5 Haiku"
  },
  // Gemini
  {
    provider: "gemini",
    model: "gemini-1.5-flash",
    displayName: "Gemini 1.5 Flash"
  },
  {
    provider: "gemini",
    model: "gemini-2.0-flash-exp",
    displayName: "Gemini 2.0 Flash"
  },
  // Groq
  {
    provider: "groq",
    model: "llama-3.3-70b-versatile",
    displayName: "Llama 3.3 70B (Groq)"
  },
  // DeepInfra
  {
    provider: "deepinfra",
    model: "meta-llama/Llama-3.3-70B-Instruct",
    displayName: "Llama 3.3 70B (DeepInfra)"
  }
];
var AVAILABLE_MODELS = FALLBACK_MODELS;
var DEFAULT_ABTESTING_CONFIG = {
  maxEmails: 100,
  timeoutMs: 3e4,
  defaultModels: FALLBACK_MODELS.slice(0, 4),
  batchSize: 10
};

// src/metrics.ts
function calculateStdDev(values, mean) {
  if (values.length === 0) return 0;
  const squaredDiffs = values.map((v) => Math.pow(v - mean, 2));
  const avgSquaredDiff = squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
  return Math.sqrt(avgSquaredDiff);
}
function computeModelStats(results, totalEmails) {
  const confidences = results.classifications.map((c) => c.confidence);
  const avgConfidence = confidences.length > 0 ? confidences.reduce((a, b) => a + b, 0) / confidences.length : 0;
  const uniqueEmailIds = new Set(results.classifications.map((c) => c.emailId));
  return {
    avgConfidence,
    minConfidence: confidences.length > 0 ? Math.min(...confidences) : 0,
    maxConfidence: confidences.length > 0 ? Math.max(...confidences) : 0,
    stdDevConfidence: calculateStdDev(confidences, avgConfidence),
    totalClassifications: results.classifications.length,
    uniqueCategories: results.stats.uniqueCategories,
    classificationRate: totalEmails > 0 ? uniqueEmailIds.size / totalEmails : 0,
    durationMs: results.timing.durationMs
  };
}
function getPrimaryClassification(classifications, emailId) {
  const emailClassifications = classifications.filter(
    (c) => c.emailId === emailId
  );
  if (emailClassifications.length === 0) return null;
  return emailClassifications.reduce(
    (best, current) => current.confidence > best.confidence ? current : best
  );
}
function computeAgreementMetrics(results, emailIds) {
  const modelKeys = Array.from(results.keys());
  let fullAgreementCount = 0;
  let partialAgreementCount = 0;
  let noAgreementCount = 0;
  for (const emailId of emailIds) {
    const categories = [];
    for (const modelKey of modelKeys) {
      const modelResults = results.get(modelKey);
      if (!modelResults) continue;
      const primary = getPrimaryClassification(
        modelResults.classifications,
        emailId
      );
      if (primary) {
        categories.push(primary.category);
      }
    }
    if (categories.length === 0) continue;
    if (categories.length < 2) continue;
    const categoryCount = /* @__PURE__ */ new Map();
    for (const cat of categories) {
      categoryCount.set(cat, (categoryCount.get(cat) || 0) + 1);
    }
    const maxCount = Math.max(...categoryCount.values());
    const totalModels = categories.length;
    if (maxCount === totalModels) {
      fullAgreementCount++;
    } else if (maxCount > totalModels / 2) {
      partialAgreementCount++;
    } else {
      noAgreementCount++;
    }
  }
  const totalClassified = fullAgreementCount + partialAgreementCount + noAgreementCount;
  const pairwiseAgreement = {};
  for (let i = 0; i < modelKeys.length; i++) {
    const modelA = modelKeys[i];
    pairwiseAgreement[modelA] = {};
    for (let j = 0; j < modelKeys.length; j++) {
      const modelB = modelKeys[j];
      if (i === j) {
        pairwiseAgreement[modelA][modelB] = 1;
        continue;
      }
      let agreements = 0;
      let comparisons = 0;
      const resultsA = results.get(modelA);
      const resultsB = results.get(modelB);
      if (!resultsA || !resultsB) continue;
      for (const emailId of emailIds) {
        const primaryA = getPrimaryClassification(
          resultsA.classifications,
          emailId
        );
        const primaryB = getPrimaryClassification(
          resultsB.classifications,
          emailId
        );
        if (primaryA && primaryB) {
          comparisons++;
          if (primaryA.category === primaryB.category) {
            agreements++;
          }
        }
      }
      pairwiseAgreement[modelA][modelB] = comparisons > 0 ? agreements / comparisons : 0;
    }
  }
  return {
    fullAgreementCount,
    partialAgreementCount,
    noAgreementCount,
    agreementRate: totalClassified > 0 ? fullAgreementCount / totalClassified : 0,
    pairwiseAgreement
  };
}
function computeCoverageMetrics(results) {
  const categoriesByModel = {};
  const allCategories = /* @__PURE__ */ new Set();
  const categoryFrequency = {};
  results.forEach((modelResults, modelKey) => {
    const modelCategories = /* @__PURE__ */ new Set();
    for (const classification of modelResults.classifications) {
      modelCategories.add(classification.category);
      allCategories.add(classification.category);
    }
    for (const category of modelCategories) {
      categoryFrequency[category] = (categoryFrequency[category] || 0) + 1;
    }
    categoriesByModel[modelKey] = Array.from(modelCategories).sort();
  });
  const modelKeys = Array.from(results.keys());
  const commonCategories = Array.from(allCategories).filter(
    (category) => modelKeys.every((key) => categoriesByModel[key]?.includes(category))
  ).sort();
  const uniqueCategories = {};
  for (const modelKey of modelKeys) {
    uniqueCategories[modelKey] = categoriesByModel[modelKey]?.filter(
      (category) => modelKeys.filter((k) => k !== modelKey).every((k) => !categoriesByModel[k]?.includes(category))
    ).sort() || [];
  }
  return {
    categoriesByModel,
    commonCategories,
    uniqueCategories,
    categoryFrequency
  };
}
function computeComparisonMetrics(results, emailIds) {
  const totalEmails = emailIds.length;
  const modelStats = {};
  results.forEach((modelResults, modelKey) => {
    modelStats[modelKey] = computeModelStats(modelResults, totalEmails);
  });
  const agreement = computeAgreementMetrics(results, emailIds);
  const coverage = computeCoverageMetrics(results);
  return {
    modelStats,
    agreement,
    coverage
  };
}
function getDisagreements(results, emailIds) {
  const disagreements = [];
  for (const emailId of emailIds) {
    const classifications = {};
    const categories = /* @__PURE__ */ new Set();
    results.forEach((modelResults, modelKey) => {
      const primary = getPrimaryClassification(
        modelResults.classifications,
        emailId
      );
      if (primary) {
        classifications[modelKey] = primary;
        categories.add(primary.category);
      }
    });
    if (Object.keys(classifications).length >= 2 && categories.size > 1) {
      disagreements.push({ emailId, classifications });
    }
  }
  return disagreements;
}
function getTopCategories(coverage, limit = 10) {
  if (!coverage?.categoryFrequency) return [];
  return Object.entries(coverage.categoryFrequency).map(([category, frequency]) => ({ category, frequency })).sort((a, b) => b.frequency - a.frequency).slice(0, limit);
}

// src/section-metrics.ts
function getUniqueEmailIds(results) {
  const emailIds = /* @__PURE__ */ new Set();
  for (const modelResults of results.values()) {
    for (const c of modelResults.classifications) {
      emailIds.add(c.emailId);
      for (const id of c.emailIds) {
        emailIds.add(id);
      }
    }
  }
  return emailIds;
}
function getClassificationsForSection(modelResults, section) {
  return modelResults.sectionBreakdown[section] || [];
}
function getTierValue(classification, depth) {
  switch (depth) {
    case 1:
      return classification.tier1;
    case 2:
      return classification.tier2;
    case 3:
      return classification.tier3;
    case 4:
      return classification.tier4;
    case 5:
      return classification.tier5;
  }
}
function getPrimaryClassificationForSection(classifications, emailId) {
  const emailClassifications = classifications.filter(
    (c) => c.emailId === emailId || c.emailIds.includes(emailId)
  );
  if (emailClassifications.length === 0) return null;
  return emailClassifications.reduce(
    (best, current) => current.confidence > best.confidence ? current : best
  );
}
function computeSectionAgreement(results, section) {
  const modelKeys = Array.from(results.keys());
  const emailIds = Array.from(getUniqueEmailIds(results));
  let fullAgreementCount = 0;
  let partialAgreementCount = 0;
  let noAgreementCount = 0;
  for (const emailId of emailIds) {
    const values = [];
    for (const modelKey of modelKeys) {
      const modelResults = results.get(modelKey);
      if (!modelResults) continue;
      const sectionClassifications = getClassificationsForSection(modelResults, section);
      const primary = getPrimaryClassificationForSection(sectionClassifications, emailId);
      if (primary) {
        values.push(primary.value);
      }
    }
    if (values.length < 2) continue;
    const valueCount = /* @__PURE__ */ new Map();
    for (const v of values) {
      valueCount.set(v, (valueCount.get(v) || 0) + 1);
    }
    const maxCount = Math.max(...valueCount.values());
    const totalModels = values.length;
    if (maxCount === totalModels) {
      fullAgreementCount++;
    } else if (maxCount > totalModels / 2) {
      partialAgreementCount++;
    } else {
      noAgreementCount++;
    }
  }
  const totalClassified = fullAgreementCount + partialAgreementCount + noAgreementCount;
  const pairwiseAgreement = {};
  for (let i = 0; i < modelKeys.length; i++) {
    const modelA = modelKeys[i];
    pairwiseAgreement[modelA] = {};
    for (let j = 0; j < modelKeys.length; j++) {
      const modelB = modelKeys[j];
      if (i === j) {
        pairwiseAgreement[modelA][modelB] = 1;
        continue;
      }
      let agreements = 0;
      let comparisons = 0;
      const resultsA = results.get(modelA);
      const resultsB = results.get(modelB);
      if (!resultsA || !resultsB) continue;
      const sectionA = getClassificationsForSection(resultsA, section);
      const sectionB = getClassificationsForSection(resultsB, section);
      for (const emailId of emailIds) {
        const primaryA = getPrimaryClassificationForSection(sectionA, emailId);
        const primaryB = getPrimaryClassificationForSection(sectionB, emailId);
        if (primaryA && primaryB) {
          comparisons++;
          if (primaryA.value === primaryB.value) {
            agreements++;
          }
        }
      }
      pairwiseAgreement[modelA][modelB] = comparisons > 0 ? agreements / comparisons : 0;
    }
  }
  return {
    fullAgreementCount,
    partialAgreementCount,
    noAgreementCount,
    agreementRate: totalClassified > 0 ? fullAgreementCount / totalClassified : 0,
    pairwiseAgreement
  };
}
function computeAllSectionAgreements(results) {
  return {
    demographics: computeSectionAgreement(results, "demographics"),
    household: computeSectionAgreement(results, "household"),
    interests: computeSectionAgreement(results, "interests"),
    purchase_intent: computeSectionAgreement(results, "purchase_intent")
  };
}
function computeHierarchyAgreement(results, depth) {
  const modelKeys = Array.from(results.keys());
  const emailIds = Array.from(getUniqueEmailIds(results));
  let fullAgreementCount = 0;
  let partialAgreementCount = 0;
  let noAgreementCount = 0;
  for (const emailId of emailIds) {
    const tierValues = [];
    for (const modelKey of modelKeys) {
      const modelResults = results.get(modelKey);
      if (!modelResults) continue;
      const allClassifications = modelResults.classifications.filter(
        (c) => c.emailId === emailId || c.emailIds.includes(emailId)
      );
      if (allClassifications.length === 0) continue;
      const primary = allClassifications.reduce(
        (best, current) => current.confidence > best.confidence ? current : best
      );
      const tierValue = getTierValue(primary, depth);
      if (tierValue) {
        tierValues.push(tierValue);
      }
    }
    if (tierValues.length < 2) continue;
    const valueCount = /* @__PURE__ */ new Map();
    for (const v of tierValues) {
      valueCount.set(v, (valueCount.get(v) || 0) + 1);
    }
    const maxCount = Math.max(...valueCount.values());
    const totalModels = tierValues.length;
    if (maxCount === totalModels) {
      fullAgreementCount++;
    } else if (maxCount > totalModels / 2) {
      partialAgreementCount++;
    } else {
      noAgreementCount++;
    }
  }
  const totalClassified = fullAgreementCount + partialAgreementCount + noAgreementCount;
  const pairwiseAgreement = {};
  for (let i = 0; i < modelKeys.length; i++) {
    const modelA = modelKeys[i];
    pairwiseAgreement[modelA] = {};
    for (let j = 0; j < modelKeys.length; j++) {
      const modelB = modelKeys[j];
      if (i === j) {
        pairwiseAgreement[modelA][modelB] = 1;
        continue;
      }
      let agreements = 0;
      let comparisons = 0;
      const resultsA = results.get(modelA);
      const resultsB = results.get(modelB);
      if (!resultsA || !resultsB) continue;
      for (const emailId of emailIds) {
        const classA = resultsA.classifications.find(
          (c) => c.emailId === emailId || c.emailIds.includes(emailId)
        );
        const classB = resultsB.classifications.find(
          (c) => c.emailId === emailId || c.emailIds.includes(emailId)
        );
        if (classA && classB) {
          const tierA = getTierValue(classA, depth);
          const tierB = getTierValue(classB, depth);
          if (tierA && tierB) {
            comparisons++;
            if (tierA === tierB) {
              agreements++;
            }
          }
        }
      }
      pairwiseAgreement[modelA][modelB] = comparisons > 0 ? agreements / comparisons : 0;
    }
  }
  return {
    fullAgreementCount,
    partialAgreementCount,
    noAgreementCount,
    agreementRate: totalClassified > 0 ? fullAgreementCount / totalClassified : 0,
    pairwiseAgreement
  };
}
function computeHierarchyMatrix(results) {
  return {
    1: computeHierarchyAgreement(results, 1),
    2: computeHierarchyAgreement(results, 2),
    3: computeHierarchyAgreement(results, 3),
    4: computeHierarchyAgreement(results, 4),
    5: computeHierarchyAgreement(results, 5)
  };
}
function computeEvidenceQualityMetrics(results) {
  const metrics = {};
  const WEAK_CONFIDENCE_THRESHOLD = 0.5;
  for (const [modelKey, modelResults] of results.entries()) {
    const classifications = modelResults.classifications;
    if (classifications.length === 0) {
      metrics[modelKey] = {
        avgConfidence: 0,
        avgEvidenceCount: 0,
        avgReasoningLength: 0,
        weakEvidenceRate: 0
      };
      continue;
    }
    const totalConfidence = classifications.reduce((sum, c) => sum + c.confidence, 0);
    const avgConfidence = totalConfidence / classifications.length;
    const totalEvidence = classifications.reduce((sum, c) => sum + c.emailIds.length, 0);
    const avgEvidenceCount = totalEvidence / classifications.length;
    const totalReasoningLength = classifications.reduce(
      (sum, c) => sum + (c.reasoning?.length || 0),
      0
    );
    const avgReasoningLength = totalReasoningLength / classifications.length;
    const weakCount = classifications.filter(
      (c) => c.confidence < WEAK_CONFIDENCE_THRESHOLD
    ).length;
    const weakEvidenceRate = weakCount / classifications.length;
    metrics[modelKey] = {
      avgConfidence,
      avgEvidenceCount,
      avgReasoningLength,
      weakEvidenceRate
    };
  }
  return metrics;
}

// src/export-import.ts
function getTimestamp() {
  return (/* @__PURE__ */ new Date()).toISOString().replace(/[:.]/g, "-").slice(0, 19);
}
function downloadJson(data, filename) {
  if (typeof document === "undefined") {
    console.log(`[export-import] Would download: ${filename}`);
    return;
  }
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json"
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
function exportStage1(emails, config) {
  const exportData = {
    version: "1.0",
    exportedAt: (/* @__PURE__ */ new Date()).toISOString(),
    downloadConfig: config,
    emails
  };
  downloadJson(exportData, `downloaded_emails_${getTimestamp()}.json`);
}
function createStage1Export(emails, config) {
  return {
    version: "1.0",
    exportedAt: (/* @__PURE__ */ new Date()).toISOString(),
    downloadConfig: config,
    emails
  };
}
async function importStage1(file) {
  const text = await file.text();
  const data = JSON.parse(text);
  if (data.version !== "1.0") {
    throw new Error(`Unsupported version: ${data.version}`);
  }
  if (!Array.isArray(data.emails)) {
    throw new Error("Invalid Stage 1 export: missing emails array");
  }
  if (!data.downloadConfig) {
    throw new Error("Invalid Stage 1 export: missing downloadConfig");
  }
  return data;
}
function parseStage1(json) {
  const data = JSON.parse(json);
  if (data.version !== "1.0") {
    throw new Error(`Unsupported version: ${data.version}`);
  }
  if (!Array.isArray(data.emails)) {
    throw new Error("Invalid Stage 1 export: missing emails array");
  }
  if (!data.downloadConfig) {
    throw new Error("Invalid Stage 1 export: missing downloadConfig");
  }
  return data;
}
function exportStage2(emails, config) {
  const exportData = {
    version: "1.0",
    exportedAt: (/* @__PURE__ */ new Date()).toISOString(),
    preprocessConfig: config,
    emails
  };
  downloadJson(exportData, `preprocessed_emails_${getTimestamp()}.json`);
}
function createStage2Export(emails, config) {
  return {
    version: "1.0",
    exportedAt: (/* @__PURE__ */ new Date()).toISOString(),
    preprocessConfig: config,
    emails
  };
}
async function importStage2(file) {
  const text = await file.text();
  const data = JSON.parse(text);
  if (data.version !== "1.0") {
    throw new Error(`Unsupported version: ${data.version}`);
  }
  if (!Array.isArray(data.emails)) {
    throw new Error("Invalid Stage 2 export: missing emails array");
  }
  if (!data.preprocessConfig) {
    throw new Error("Invalid Stage 2 export: missing preprocessConfig");
  }
  for (const email of data.emails) {
    if (typeof email.summary !== "string") {
      throw new Error(
        `Invalid Stage 2 export: email ${email.id} missing summary`
      );
    }
  }
  return data;
}
function parseStage2(json) {
  const data = JSON.parse(json);
  if (data.version !== "1.0") {
    throw new Error(`Unsupported version: ${data.version}`);
  }
  if (!Array.isArray(data.emails)) {
    throw new Error("Invalid Stage 2 export: missing emails array");
  }
  if (!data.preprocessConfig) {
    throw new Error("Invalid Stage 2 export: missing preprocessConfig");
  }
  for (const email of data.emails) {
    if (typeof email.summary !== "string") {
      throw new Error(
        `Invalid Stage 2 export: email ${email.id} missing summary`
      );
    }
  }
  return data;
}
function exportStage3(models, results, comparisonMetrics, sourceStage2File) {
  const resultsRecord = {};
  results.forEach((value, key) => {
    resultsRecord[key] = value;
  });
  const exportData = {
    version: "1.0",
    exportedAt: (/* @__PURE__ */ new Date()).toISOString(),
    sourceStage2File,
    models,
    results: resultsRecord,
    comparisonMetrics
  };
  downloadJson(exportData, `classification_results_${getTimestamp()}.json`);
}
function createStage3Export(models, results, comparisonMetrics, sourceStage2File) {
  const resultsRecord = {};
  results.forEach((value, key) => {
    resultsRecord[key] = value;
  });
  return {
    version: "1.0",
    exportedAt: (/* @__PURE__ */ new Date()).toISOString(),
    sourceStage2File,
    models,
    results: resultsRecord,
    comparisonMetrics
  };
}
async function importStage3(file) {
  const text = await file.text();
  const data = JSON.parse(text);
  if (data.version !== "1.0") {
    throw new Error(`Unsupported version: ${data.version}`);
  }
  if (!Array.isArray(data.models)) {
    throw new Error("Invalid Stage 3 export: missing models array");
  }
  if (!data.results || typeof data.results !== "object") {
    throw new Error("Invalid Stage 3 export: missing results");
  }
  if (!data.comparisonMetrics) {
    throw new Error("Invalid Stage 3 export: missing comparisonMetrics");
  }
  return data;
}
function parseStage3(json) {
  const data = JSON.parse(json);
  if (data.version !== "1.0") {
    throw new Error(`Unsupported version: ${data.version}`);
  }
  if (!Array.isArray(data.models)) {
    throw new Error("Invalid Stage 3 export: missing models array");
  }
  if (!data.results || typeof data.results !== "object") {
    throw new Error("Invalid Stage 3 export: missing results");
  }
  if (!data.comparisonMetrics) {
    throw new Error("Invalid Stage 3 export: missing comparisonMetrics");
  }
  return data;
}
function resultsRecordToMap(results) {
  return new Map(Object.entries(results));
}
function serializeState(state) {
  const serializable = {
    ...state,
    classificationResults: Object.fromEntries(state.classificationResults)
  };
  return JSON.stringify(serializable);
}
function deserializeState(json) {
  const parsed = JSON.parse(json);
  return {
    ...parsed,
    classificationResults: new Map(
      Object.entries(parsed.classificationResults || {})
    )
  };
}
export {
  AVAILABLE_MODELS,
  DEFAULT_ABTESTING_CONFIG,
  FALLBACK_MODELS,
  computeAgreementMetrics,
  computeAllSectionAgreements,
  computeComparisonMetrics,
  computeCoverageMetrics,
  computeEvidenceQualityMetrics,
  computeHierarchyAgreement,
  computeHierarchyMatrix,
  computeModelStats,
  computeSectionAgreement,
  createStage1Export,
  createStage2Export,
  createStage3Export,
  deserializeState,
  exportStage1,
  exportStage2,
  exportStage3,
  getDisagreements,
  getTopCategories,
  importStage1,
  importStage2,
  importStage3,
  parseStage1,
  parseStage2,
  parseStage3,
  resultsRecordToMap,
  serializeState
};
